import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Col,
  Empty,
  Popconfirm,
  Row,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Upload,
  message,
} from 'antd';
import { ArrowLeftOutlined, CameraOutlined, DeleteOutlined, DownloadOutlined, MobileOutlined } from '@ant-design/icons';
import ImgCrop from 'antd-img-crop';
import { Column, Pie } from '@ant-design/charts';

import { getDriver, updateDriver, getDriverStats } from '../modules/drivers/api';
import { uploadSharedFile } from '../modules/orders/api';
import RiderForm from '../components/Forms/RiderForm';
import PartnerLogo from '../components/PartnerLogo';
import DriverAvatar from '../components/DriverAvatar';
import { openDriverAppAsDriver } from '../utils/openDriverApp';
import StatsFilters from '../components/StatsFilters';
import { downloadDriverStatsCsv } from '../utils/driverStatsCsvExport';

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const STATUS_COLORS = {
  COMPLETED: '#2e7d32',
  DELIVERY_FAILED: '#c62828',
  PICKUP_FAILED: '#c62828',
  SKIPPED: '#f9a825',
  ASSIGNED: '#1565c0',
  ERROR: '#b71c1c',
};
const fmtMins = (m) => {
  if (!m || m <= 0) return '0m';
  const h = Math.floor(m / 60);
  const mm = Math.round(m % 60);
  return h > 0 ? `${h}h ${mm}m` : `${mm}m`;
};

const DriverDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t: tc } = useTranslation('common');
  const [messageApi, contextHolder] = message.useMessage();
  const [impersonating, setImpersonating] = useState(false);

  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoUploading, setPhotoUploading] = useState(false);

  const [filter, setFilter] = useState({ period: 'this_month', from: undefined, to: undefined });
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const loadDriver = useCallback(async () => {
    setLoading(true);
    try {
      const { response } = await getDriver(id);
      setDriver(response || null);
    } catch (e) {
      messageApi.error(e?.message || tc('error'));
    } finally {
      setLoading(false);
    }
  }, [id, messageApi, tc]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const { response } = await getDriverStats(id, {
        period: filter.period,
        from: filter.from,
        to: filter.to,
      });
      setStats(response || null);
    } catch (e) {
      messageApi.error(e?.message || tc('error'));
    } finally {
      setStatsLoading(false);
    }
  }, [id, filter.period, filter.from, filter.to, messageApi, tc]);

  useEffect(() => {
    loadDriver();
  }, [loadDriver]);
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // ─── Photo ────────────────────────────────────────────────────────────────
  const handlePhotoUpload = async (file) => {
    setPhotoUploading(true);
    try {
      const { response: uploaded } = await uploadSharedFile(file);
      if (!uploaded?.id) throw new Error('upload failed');
      await updateDriver({ id: Number(id), photoFileId: uploaded.id });
      messageApi.success('Photo updated');
      loadDriver();
    } catch (e) {
      messageApi.error(e?.message || 'Could not upload the photo');
    } finally {
      setPhotoUploading(false);
    }
    return false; // prevent antd default upload
  };
  const handleRemovePhoto = async () => {
    try {
      await updateDriver({ id: Number(id), photoFileId: null });
      messageApi.success('Photo removed');
      loadDriver();
    } catch (e) {
      messageApi.error(e?.message || tc('error'));
    }
  };

  // ─── Edit ─────────────────────────────────────────────────────────────────
  const handleEditSubmit = async (params) => {
    try {
      await updateDriver({ id: Number(id), ...params });
      messageApi.success(tc('updated', { defaultValue: 'Updated' }));
      loadDriver();
    } catch (e) {
      messageApi.error(e?.message || tc('error'));
    }
  };

  if (loading && !driver) {
    return <Spin style={{ display: 'block', paddingTop: 96 }} />;
  }
  if (!driver) {
    return (
      <div className="Content" style={{ paddingTop: 80 }}>
        <Empty description="Driver not found" />
      </div>
    );
  }

  const photoKey = driver.photoFile?.key;

  const overviewTab = (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={8}>
        <Card title="Photo" size="small">
          <div style={{ textAlign: 'center' }}>
            <DriverAvatar name={driver.name} photoKey={photoKey} size={120} />
            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'center' }}>
              <ImgCrop rotationSlider aspect={1} modalTitle="Crop photo">
                <Upload accept="image/*" beforeUpload={handlePhotoUpload} maxCount={1} showUploadList={false}>
                  <Button icon={<CameraOutlined />} loading={photoUploading}>
                    {photoKey ? 'Replace' : 'Upload photo'}
                  </Button>
                </Upload>
              </ImgCrop>
              {photoKey && (
                <Popconfirm
                  title="Remove the photo?"
                  onConfirm={handleRemovePhoto}
                  okText={tc('yes')}
                  cancelText={tc('no')}
                >
                  <Button danger icon={<DeleteOutlined />} />
                </Popconfirm>
              )}
            </div>
          </div>
        </Card>
      </Col>
      <Col xs={24} md={16}>
        <Card title="Details" size="small">
          <RiderForm
            action="edit"
            rider={driver}
            isFetching={false}
            handleSubmit={handleEditSubmit}
            handleCancel={() => navigate('/admin/drivers')}
          />
        </Card>
      </Col>
    </Row>
  );

  // ─── Stats tab ──────────────────────────────────────────────────────────
  const d = stats?.delivery;
  const r = stats?.routes;
  const statusData = Object.entries(stats?.activity?.ordersByStatus || {}).map(([status, value]) => ({
    type: status.replace(/_/g, ' '),
    value,
    color: STATUS_COLORS[status] || '#9e9e9e',
  }));
  const dayData = (stats?.activity?.ordersByDayOfWeek || []).map((v, i) => ({ day: DOW[i], value: v }));
  const partnerColumns = [
    {
      title: 'Partner',
      dataIndex: 'name',
      key: 'name',
      render: (n) => (
        <span>
          <PartnerLogo name={n} logoKey={null} size={16} style={{ marginRight: 6 }} />
          {n}
        </span>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      sorter: (a, b) => a.total - b.total,
      defaultSortOrder: 'descend',
    },
    { title: 'Completed', dataIndex: 'completed', key: 'completed' },
    { title: 'Failed', dataIndex: 'failed', key: 'failed' },
    { title: 'Skipped', dataIndex: 'skipped', key: 'skipped' },
    {
      title: 'Completion %',
      dataIndex: 'completionRate',
      key: 'completionRate',
      render: (v) => <Tag color={v >= 90 ? 'green' : v >= 70 ? 'orange' : 'red'}>{v}%</Tag>,
    },
  ];

  const statsTab = (
    <div>
      <StatsFilters
        value={filter}
        onChange={setFilter}
        resolvedRange={stats?.period}
        rightSlot={
          <Button
            icon={<DownloadOutlined />}
            disabled={!stats || statsLoading}
            onClick={() => downloadDriverStatsCsv(stats, { driverName: driver.name })}
          >
            Export CSV
          </Button>
        }
      />
      <Spin spinning={statsLoading}>
        {!stats || d?.total === 0 ? (
          <Empty description="No activity in this period" style={{ paddingTop: 48 }} />
        ) : (
          <>
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col xs={12} md={5}>
                <Card size="small">
                  <Statistic title="Deliveries" value={d.total} />
                </Card>
              </Col>
              <Col xs={12} md={5}>
                <Card size="small">
                  <Statistic title="Completed" value={d.completed} valueStyle={{ color: '#2e7d32' }} />
                </Card>
              </Col>
              <Col xs={12} md={5}>
                <Card size="small">
                  <Statistic title="Failed" value={d.failed} valueStyle={{ color: '#c62828' }} />
                </Card>
              </Col>
              <Col xs={12} md={4}>
                <Card size="small">
                  <Statistic title="Skipped" value={d.skipped} valueStyle={{ color: '#f9a825' }} />
                </Card>
              </Col>
              <Col xs={24} md={5}>
                <Card size="small">
                  <Statistic title="Completion" value={d.completionRate} suffix="%" />
                </Card>
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginTop: 4 }}>
              <Col xs={12} md={4}>
                <Card size="small">
                  <Statistic title="Routes" value={r.total} />
                </Card>
              </Col>
              <Col xs={12} md={4}>
                <Card size="small">
                  <Statistic title="Distance" value={r.totalDistanceKm} suffix="km" />
                </Card>
              </Col>
              <Col xs={12} md={4}>
                <Card size="small">
                  <Statistic title="Driven time" value={fmtMins(r.totalDrivenMins)} />
                </Card>
              </Col>
              <Col xs={12} md={4}>
                <Card size="small">
                  <Statistic title="Client service" value={fmtMins(r.totalClientServiceMins)} />
                </Card>
              </Col>
              {/* P1 Pause/Resume — excluded from driven time + client service. Always
                  shown (0m included) so it's visible that pauses are accounted for. */}
              <Col xs={12} md={4}>
                <Card size="small">
                  <Tooltip title="Badged pauses — excluded from driven time and client service">
                    <Statistic
                      title="⏸ Paused"
                      value={fmtMins(r.totalPausedMins ?? 0)}
                      suffix={
                        r.routesWithPause ? <span style={{ fontSize: 12 }}>· {r.routesWithPause} route(s)</span> : null
                      }
                    />
                  </Tooltip>
                </Card>
              </Col>
              <Col xs={24} md={4}>
                <Card size="small" style={{ background: 'linear-gradient(135deg,#f1f8f1,#fff)' }}>
                  <Statistic
                    title="🌱 CO₂ saved vs car"
                    value={r.totalCo2SavedKg}
                    suffix="kg"
                    valueStyle={{ color: '#2e7d32' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Start punctuality — does the driver press "Start route" when the
                shift begins? (badged start vs planned; late badge == late start) */}
            {stats.punctuality && stats.punctuality.routesWithStart + stats.punctuality.startNotBadged > 0 && (
              <Row gutter={[16, 16]} style={{ marginTop: 4 }}>
                <Col xs={12} md={5}>
                  <Card size="small">
                    <Statistic
                      title={`⏰ On-time starts (≤${stats.punctuality.thresholdMins}′)`}
                      value={stats.punctuality.onTimeStartRate ?? '—'}
                      suffix={stats.punctuality.onTimeStartRate != null ? '%' : ''}
                      valueStyle={{
                        color:
                          stats.punctuality.onTimeStartRate == null
                            ? undefined
                            : stats.punctuality.onTimeStartRate >= 80
                              ? '#2e7d32'
                              : stats.punctuality.onTimeStartRate >= 50
                                ? '#f9a825'
                                : '#c62828',
                      }}
                    />
                  </Card>
                </Col>
                <Col xs={12} md={5}>
                  <Card size="small">
                    <Statistic
                      title="Avg start delay"
                      value={stats.punctuality.avgStartDelayMins ?? '—'}
                      suffix={stats.punctuality.avgStartDelayMins != null ? '′' : ''}
                      valueStyle={{
                        color:
                          (stats.punctuality.avgStartDelayMins ?? 0) > stats.punctuality.thresholdMins
                            ? '#c62828'
                            : '#2e7d32',
                      }}
                    />
                  </Card>
                </Col>
                <Col xs={12} md={5}>
                  <Card size="small">
                    <Statistic title="Late starts" value={stats.punctuality.lateStarts} />
                  </Card>
                </Col>
                <Col xs={12} md={5}>
                  <Card size="small">
                    <Statistic
                      title="Start not badged"
                      value={stats.punctuality.startNotBadged}
                      valueStyle={{ color: stats.punctuality.startNotBadged > 0 ? '#c62828' : undefined }}
                    />
                  </Card>
                </Col>
                {stats.punctuality.worstStartDelayMins != null && (
                  <Col xs={12} md={4}>
                    <Card size="small">
                      <Statistic title="Worst delay" value={stats.punctuality.worstStartDelayMins} suffix="′" />
                    </Card>
                  </Col>
                )}
              </Row>
            )}

            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col xs={24} md={12}>
                <Card title="Deliveries by status" size="small">
                  {statusData.length ? (
                    <Pie
                      data={statusData}
                      angleField="value"
                      colorField="type"
                      radius={0.85}
                      height={240}
                      legend={{ position: 'bottom' }}
                      color={({ type }) => statusData.find((s) => s.type === type)?.color || '#9e9e9e'}
                    />
                  ) : (
                    <Empty />
                  )}
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card title="Deliveries by day of week" size="small">
                  <Column
                    data={dayData}
                    xField="day"
                    yField="value"
                    height={240}
                    columnWidthRatio={0.6}
                    color="#1565c0"
                  />
                </Card>
              </Col>
            </Row>

            <Card title="By partner" size="small" style={{ marginTop: 16 }}>
              <Table
                rowKey={(row) => `${row.name}-${row.code}`}
                dataSource={stats.byPartner?.rows || []}
                columns={partnerColumns}
                pagination={false}
                size="small"
              />
            </Card>
          </>
        )}
      </Spin>
    </div>
  );

  return (
    <div className="DriverDetailPage Content" style={{ paddingBottom: 24 }}>
      {contextHolder}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/drivers')}>
          {tc('back', { defaultValue: 'Back' })}
        </Button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <DriverAvatar name={driver.name} photoKey={photoKey} size={48} />
        <div>
          <h2 style={{ margin: 0 }}>{driver.name}</h2>
          <div style={{ color: 'var(--text-muted)' }}>{driver.email || driver.phone || '—'}</div>
        </div>
        {/* Aug 2026 — REAL driver-app login as this driver (support tool).
            Actions in the opened tab persist; the app shows a red banner. */}
        <div style={{ marginLeft: 'auto' }}>
          <Popconfirm
            title="Log into the driver app as this driver?"
            description="Opens a new tab with a REAL driver session — everything you do there counts as the driver (4h, audited)."
            okText="Open driver app"
            cancelText="Cancel"
            onConfirm={async () => {
              setImpersonating(true);
              await openDriverAppAsDriver(driver.id, messageApi);
              setImpersonating(false);
            }}
          >
            <Button icon={<MobileOutlined />} loading={impersonating}>
              Log in as driver
            </Button>
          </Popconfirm>
        </div>
      </div>

      <Tabs
        defaultActiveKey="overview"
        items={[
          { key: 'overview', label: 'Overview', children: overviewTab },
          { key: 'stats', label: 'Stats', children: statsTab },
        ]}
      />
    </div>
  );
};

export default DriverDetailPage;
